// @LTG-ID: F-001
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 07_PAGES_AND_ROUTES.md#layout-chrome, 08_FEATURES.md#F-001
// @CHANGED: 2026-02 session_002 / CHG-0099 (Amendments 2 + 4 — Log in CTA + mobile hamburger)
// @OWNER: web
//
// Sticky glass header. CHG-0099 swapped the legacy Snapshot price CTA for "Begin"
// → app.letstalkgrowth.com/begin via the centralized ExternalCTA + cta-ids.js.
// Amendment 4 introduced a hamburger menu at ≤767px so the inline nav links
// (Journals / Refund Policy / Contact) collapse cleanly on mobile.
// Visual reference: /app/source_reference/Landing Page.html lines 1207-1232.

import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import ExternalCTA from "../../lib/external-cta";
import "./header.css";

export const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const hamburgerRef = useRef(null);
    const menuRef = useRef(null);
    const location = useLocation();

    // Close menu whenever the route changes (clicking a nav link inside the menu).
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    // ESC + focus trap + body scroll lock + auto-focus first menu item on open.
    useEffect(() => {
        if (!isMenuOpen) {
            document.body.style.overflow = "";
            return;
        }
        document.body.style.overflow = "hidden";

        const handleKey = (e) => {
            if (e.key === "Escape") {
                setIsMenuOpen(false);
                hamburgerRef.current?.focus();
                return;
            }
            if (e.key === "Tab" && menuRef.current) {
                const focusables = menuRef.current.querySelectorAll(
                    'a[href], button:not([disabled])',
                );
                if (!focusables.length) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener("keydown", handleKey);

        const focusTimer = setTimeout(() => {
            const firstFocusable = menuRef.current?.querySelector(
                'a[href], button:not([disabled])',
            );
            firstFocusable?.focus();
        }, 50);

        return () => {
            document.removeEventListener("keydown", handleKey);
            clearTimeout(focusTimer);
            document.body.style.overflow = "";
        };
    }, [isMenuOpen]);

    const closeMenu = () => {
        setIsMenuOpen(false);
        hamburgerRef.current?.focus();
    };

    return (
        <header className="ltg-header" data-testid="site-header">
            <div className="ltg-header__inner">
                <Link
                    to="/"
                    className="ltg-header__logo"
                    data-testid="site-header-logo"
                    aria-label="Let's Talk Growth — home"
                >
                    <img
                        src="/assets/logo-bubble.png"
                        alt=""
                        className="ltg-header__bubble"
                    />
                    <img
                        src="/assets/logo-wordmark.png"
                        alt="KGOB CPAs and Advisors"
                        className="ltg-header__wordmark"
                    />
                </Link>

                <button
                    ref={hamburgerRef}
                    type="button"
                    className="ltg-header__hamburger"
                    aria-label={
                        isMenuOpen
                            ? "Close navigation menu"
                            : "Open navigation menu"
                    }
                    aria-expanded={isMenuOpen}
                    aria-controls="ltg-mobile-menu"
                    onClick={() => setIsMenuOpen((v) => !v)}
                    data-testid="site-header-hamburger"
                >
                    {isMenuOpen ? (
                        <svg
                            viewBox="0 0 24 24"
                            width="22"
                            height="22"
                            aria-hidden="true"
                        >
                            <path
                                d="M6 6L18 18M18 6L6 18"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    ) : (
                        <svg
                            viewBox="0 0 24 24"
                            width="22"
                            height="22"
                            aria-hidden="true"
                        >
                            <path
                                d="M4 7H20M4 12H20M4 17H20"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    )}
                </button>

                <nav
                    className="ltg-header__nav"
                    aria-label="Primary"
                    data-testid="site-header-nav"
                >
                    <Link to="/journals" data-testid="site-header-nav-journals">
                        Journals
                    </Link>
                    <Link to="/contact" data-testid="site-header-nav-contact">
                        Contact
                    </Link>
                </nav>

                <div
                    className="ltg-header__cta-group"
                    data-testid="site-header-cta-group"
                >
                    <ExternalCTA
                        ctaId="header-login"
                        className="ltg-header__login"
                    >
                        Log in
                    </ExternalCTA>
                    <ExternalCTA
                        ctaId="header-order"
                        className="ltg-header__cta"
                    >
                        Get Your Valuation{" "}
                        <span className="ltg-arrow" aria-hidden="true">
                            &rarr;
                        </span>
                    </ExternalCTA>
                </div>
            </div>

            {isMenuOpen && (
                <>
                    <button
                        type="button"
                        className="ltg-header__backdrop"
                        onClick={closeMenu}
                        aria-label="Close navigation menu"
                        tabIndex={-1}
                        data-testid="site-header-backdrop"
                    />
                    <div
                        ref={menuRef}
                        id="ltg-mobile-menu"
                        className="ltg-header__mobile-menu"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Navigation menu"
                        data-testid="site-header-mobile-menu"
                    >
                        <div className="ltg-header__mobile-menu-head">
                            <span className="ltg-header__mobile-menu-eyebrow">
                                Menu
                            </span>
                            <button
                                type="button"
                                className="ltg-header__mobile-menu-close"
                                onClick={closeMenu}
                                aria-label="Close navigation menu"
                                data-testid="site-header-mobile-menu-close"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    width="22"
                                    height="22"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M6 6L18 18M18 6L6 18"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        </div>
                        <nav
                            className="ltg-header__mobile-nav"
                            aria-label="Mobile primary"
                        >
                            <Link
                                to="/journals"
                                data-testid="site-header-mobile-nav-journals"
                            >
                                Journals
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="site-header-mobile-nav-contact"
                            >
                                Contact
                            </Link>
                        </nav>
                        <div className="ltg-header__mobile-cta-group">
                            <ExternalCTA
                                ctaId="header-login"
                                className="ltg-header__mobile-login"
                            >
                                Log in
                            </ExternalCTA>
                            <ExternalCTA
                                ctaId="header-order"
                                className="ltg-header__mobile-cta"
                            >
                                Get Your Valuation{" "}
                                <span className="ltg-arrow" aria-hidden="true">
                                    &rarr;
                                </span>
                            </ExternalCTA>
                        </div>
                    </div>
                </>
            )}
        </header>
    );
};

export default Header;
